/** Parts joined by middle dots, wrapping as whole parts with no dot left at a line's end or
 *  start (live pass at 390 px: a record header's "Curated Planet ·" ended a line and
 *  "· ID 630" began the next; the images tab's "drag to reorder ·" left its dot on a line of
 *  its own).
 *
 *  - Each part after the first carries its dot in a fixed room before it, so a dot never
 *    ends a line.
 *  - The row is pulled left by that room and clipped at its own left edge: the dot of a part
 *    that starts a line falls in the clipped room, so no dot starts a line either. The clip
 *    keeps 3 px for a focused trigger's ring (2 px + 1 px offset) and clips nothing above,
 *    below or to the right.
 *  - A part stays on one line; only a part wider than the row breaks inside itself.
 *  - The dots stay text: the row reads "Acme · Tour X · ID 7" as before. */
import { Box } from '@mui/material';
import type { ReactNode } from 'react';

/** The dot's room: what " · " takes in the running text (11.9 px at 14 px Inter). */
const DOT_ROOM = '0.85em';

/** Whether a part is shown: falsy parts (`null`, `false`, `''`) are skipped, so a caller can
 *  pass optional pieces without conditionals. */
export const isShownPart = (p: ReactNode) => p !== null && p !== undefined && p !== false && p !== '';

export function DotSeparated({ parts }: { parts: ReactNode[] }) {
  const shown = parts.filter(isShownPart);
  return (
    <Box
      component="span"
      data-dot-separated
      sx={{ display: 'block', minWidth: 0, clipPath: 'inset(-100vh -100vw -100vh -3px)' }}
    >
      <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', ml: `-${DOT_ROOM}` }}>
        {shown.map((p, i) => (
          // Positional keys are fine: the list is static per render.
          // eslint-disable-next-line react/no-array-index-key
          <Box component="span" key={i} data-dot-part sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0, maxWidth: '100%' }}>
            <Box component="span" data-dot sx={{ flex: 'none', width: DOT_ROOM, textAlign: 'center' }}>
              {i > 0 && ' · '}
            </Box>
            <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {p}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
